import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import type { Readable } from 'stream';

// Disable default body parser for multipart/form-data
export const config = {
  api: {
    bodyParser: false,
  },
};

// ─── Inline types (fallback if lib/bank/types not available at compile time) ───
interface ParsedMovement {
  fecha_operacion: string;
  fecha_valor?: string;
  concepto?: string;
  concepto_complementario?: string;
  importe: number;
  saldo_posterior?: number;
  referencia?: string;
  tipo_movimiento?: string;
  datos_raw?: Record<string, unknown>;
}

// ─── Helper: read raw body from request stream ───
async function readRawBody(req: NextApiRequest): Promise<Buffer> {
  const chunks: Buffer[] = [];
  for await (const chunk of req as unknown as Readable) {
    chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
  }
  return Buffer.concat(chunks);
}

// ─── Helper: parse multipart form data (simple implementation) ───
interface ParsedFormData {
  fields: Record<string, string>;
  file?: { filename: string; content: Buffer; contentType: string };
}

async function parseMultipart(req: NextApiRequest): Promise<ParsedFormData> {
  const contentType = req.headers['content-type'] || '';
  const boundaryMatch = contentType.match(/boundary=(.+)/);
  if (!boundaryMatch) {
    throw new Error('No se encontró boundary en el Content-Type multipart.');
  }

  const boundary = boundaryMatch[1];
  const rawBody = await readRawBody(req);
  const bodyStr = rawBody.toString('latin1');
  const parts = bodyStr.split(`--${boundary}`).filter(
    (p) => p.trim() !== '' && p.trim() !== '--'
  );

  const result: ParsedFormData = { fields: {} };

  for (const part of parts) {
    const headerEnd = part.indexOf('\r\n\r\n');
    if (headerEnd === -1) continue;

    const headerSection = part.substring(0, headerEnd);
    const bodySection = part.substring(headerEnd + 4).replace(/\r\n$/, '');

    const nameMatch = headerSection.match(/name="([^"]+)"/);
    if (!nameMatch) continue;
    const fieldName = nameMatch[1];

    const filenameMatch = headerSection.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      // File field
      const contentTypeMatch = headerSection.match(/Content-Type:\s*(.+)/i);
      const start = rawBody.indexOf(Buffer.from('\r\n\r\n', 'latin1'), rawBody.indexOf(Buffer.from(headerSection.substring(0, 40), 'latin1'))) + 4;
      const endMarker = Buffer.from(`\r\n--${boundary}`, 'latin1');
      const end = rawBody.indexOf(endMarker, start);
      result.file = {
        filename: filenameMatch[1],
        content: rawBody.subarray(start, end !== -1 ? end : undefined),
        contentType: contentTypeMatch ? contentTypeMatch[1].trim() : 'application/octet-stream',
      };
    } else {
      // Regular field
      result.fields[fieldName] = bodySection;
    }
  }

  return result;
}

// ─── Inline Norma43 parser (fallback) ───
function parseNorma43(content: string): ParsedMovement[] {
  const lines = content.split('\n').map((l) => l.trimEnd());
  const movements: ParsedMovement[] = [];

  for (const line of lines) {
    // Record type 22: movement main record
    if (line.startsWith('22')) {
      try {
        const fechaStr = line.substring(10, 16); // AAMMDD
        const year = 2000 + parseInt(fechaStr.substring(0, 2), 10);
        const month = parseInt(fechaStr.substring(2, 4), 10) - 1;
        const day = parseInt(fechaStr.substring(4, 6), 10);
        const fecha = new Date(year, month, day);

        const fechaValorStr = line.substring(16, 22);
        const yearV = 2000 + parseInt(fechaValorStr.substring(0, 2), 10);
        const monthV = parseInt(fechaValorStr.substring(2, 4), 10) - 1;
        const dayV = parseInt(fechaValorStr.substring(4, 6), 10);
        const fechaValor = new Date(yearV, monthV, dayV);

        const signo = line.substring(27, 28); // 1=debit, 2=credit
        const importeStr = line.substring(28, 42);
        const importe = parseInt(importeStr, 10) / 100;
        const importeFinal = signo === '1' ? -importe : importe;

        const concepto = line.substring(52).trim();
        const referencia = line.substring(42, 52).trim();

        movements.push({
          fecha_operacion: fecha.toISOString().split('T')[0],
          fecha_valor: fechaValor.toISOString().split('T')[0],
          concepto: concepto || undefined,
          importe: importeFinal,
          referencia: referencia || undefined,
          tipo_movimiento: importeFinal >= 0 ? 'ingreso' : 'pago',
          datos_raw: { linea_raw: line, formato: 'norma43' },
        });
      } catch {
        // Skip malformed lines
      }
    }
    // Record type 23: complementary concept
    if (line.startsWith('23') && movements.length > 0) {
      const complemento = line.substring(4).trim();
      if (complemento) {
        const last = movements[movements.length - 1];
        last.concepto_complementario = last.concepto_complementario
          ? `${last.concepto_complementario} ${complemento}`
          : complemento;
      }
    }
  }

  return movements;
}

// ─── Inline CSV parser (fallback) ───
function parseCSV(content: string): ParsedMovement[] {
  const lines = content.split('\n').filter((l) => l.trim());
  if (lines.length < 2) return []; // Need header + at least one row

  const header = lines[0].split(/[;,\t]/).map((h) => h.trim().toLowerCase().replace(/"/g, ''));
  const movements: ParsedMovement[] = [];

  // Try to identify columns
  const fechaIdx = header.findIndex((h) =>
    ['fecha', 'date', 'fecha_operacion', 'fecha operacion', 'f.operación', 'f.operacion'].includes(h)
  );
  const conceptoIdx = header.findIndex((h) =>
    ['concepto', 'descripcion', 'description', 'detalle', 'concepto/descripción'].includes(h)
  );
  const importeIdx = header.findIndex((h) =>
    ['importe', 'amount', 'cantidad', 'monto', 'importe (eur)'].includes(h)
  );
  const saldoIdx = header.findIndex((h) =>
    ['saldo', 'balance', 'saldo_posterior', 'saldo posterior'].includes(h)
  );

  if (fechaIdx === -1 || importeIdx === -1) {
    // Fallback: assume first col=date, second=concept, third=amount
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(/[;,\t]/).map((c) => c.trim().replace(/"/g, ''));
      if (cols.length < 3) continue;
      const importe = parseFloat(cols[2].replace(/\./g, '').replace(',', '.'));
      if (isNaN(importe)) continue;
      movements.push({
        fecha_operacion: cols[0],
        concepto: cols[1] || undefined,
        importe,
        tipo_movimiento: importe >= 0 ? 'ingreso' : 'pago',
        datos_raw: { linea_raw: lines[i], formato: 'csv' },
      });
    }
    return movements;
  }

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(/[;,\t]/).map((c) => c.trim().replace(/"/g, ''));
    const importeStr = cols[importeIdx];
    const importe = parseFloat(importeStr.replace(/\./g, '').replace(',', '.'));
    if (isNaN(importe)) continue;

    const mov: ParsedMovement = {
      fecha_operacion: cols[fechaIdx],
      concepto: conceptoIdx !== -1 ? cols[conceptoIdx] : undefined,
      importe,
      saldo_posterior: saldoIdx !== -1 ? parseFloat(cols[saldoIdx].replace(/\./g, '').replace(',', '.')) || undefined : undefined,
      tipo_movimiento: importe >= 0 ? 'ingreso' : 'pago',
      datos_raw: { linea_raw: lines[i], formato: 'csv' },
    };

    movements.push(mov);
  }

  return movements;
}

/**
 * Bank Import API
 * POST /api/bank/import?bank_account_id=X&formato=norma43|csv|excel
 *
 * Accepts multipart/form-data with a file field.
 * Parses bank movements and inserts them, detecting duplicates.
 * Returns: { importados, duplicados, errores }
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    const { bank_account_id, formato } = req.query;

    if (!bank_account_id) {
      return res.status(400).json({ success: false, error: 'bank_account_id es obligatorio.' });
    }

    const formatoStr = (formato as string) || 'csv';
    if (!['norma43', 'csv', 'excel'].includes(formatoStr)) {
      return res.status(400).json({
        success: false,
        error: 'Formato no soportado. Usa: norma43, csv, excel.',
      });
    }

    // Verify bank account exists
    const { data: account, error: accountError } = await supabaseAdmin
      .from('bank_accounts')
      .select('id, empresa_id')
      .eq('id', bank_account_id as string)
      .eq('activa', true)
      .maybeSingle();

    if (accountError || !account) {
      return res.status(404).json({
        success: false,
        error: 'Cuenta bancaria no encontrada o inactiva.',
      });
    }

    // Parse multipart form data
    let fileContent: string;
    let fileName: string;

    try {
      const formData = await parseMultipart(req);
      if (!formData.file) {
        return res.status(400).json({ success: false, error: 'No se proporcionó archivo.' });
      }
      fileContent = formData.file.content.toString('utf-8');
      fileName = formData.file.filename;
    } catch (parseError: any) {
      return res.status(400).json({
        success: false,
        error: `Error al leer el archivo: ${parseError.message}`,
      });
    }

    // Parse movements based on format
    let parsedMovements: ParsedMovement[];
    const errores: string[] = [];

    try {
      // Try to import external parser first, fallback to inline
      let parserModule: { parse?: (content: string) => ParsedMovement[] } | null = null;
      try {
        if (formatoStr === 'norma43') {
          parserModule = await import('../../../lib/bank/norma43-parser');
        } else if (formatoStr === 'csv' || formatoStr === 'excel') {
          parserModule = await import('../../../lib/bank/csv-bank-parser');
        }
      } catch {
        // External parser not available, use inline fallback
      }

      if (parserModule?.parse) {
        parsedMovements = parserModule.parse(fileContent);
      } else if (formatoStr === 'norma43') {
        parsedMovements = parseNorma43(fileContent);
      } else {
        parsedMovements = parseCSV(fileContent);
      }
    } catch (parseError: any) {
      errores.push(`Error parsing ${formatoStr}: ${parseError.message}`);
      parsedMovements = [];
    }

    if (parsedMovements.length === 0 && errores.length === 0) {
      errores.push('No se encontraron movimientos en el archivo.');
    }

    // Detect duplicates and insert
    let importados = 0;
    let duplicados = 0;

    for (const mov of parsedMovements) {
      try {
        // Check for duplicate: same fecha + importe + concepto
        const { data: existing } = await supabaseAdmin
          .from('bank_movements')
          .select('id')
          .eq('bank_account_id', bank_account_id as string)
          .eq('fecha_operacion', mov.fecha_operacion)
          .eq('importe', mov.importe)
          .eq('concepto', mov.concepto || '')
          .maybeSingle();

        if (existing) {
          duplicados++;
          continue;
        }

        // Insert movement
        const { error: insertError } = await supabaseAdmin
          .from('bank_movements')
          .insert({
            bank_account_id: bank_account_id as string,
            empresa_id: account.empresa_id,
            fecha_operacion: mov.fecha_operacion,
            fecha_valor: mov.fecha_valor || null,
            concepto: mov.concepto || null,
            concepto_complementario: mov.concepto_complementario || null,
            importe: mov.importe,
            saldo_posterior: mov.saldo_posterior ?? null,
            referencia: mov.referencia || null,
            tipo_movimiento: mov.tipo_movimiento || 'otro',
            estado_conciliacion: 'pendiente',
            origen_importacion: formatoStr,
            datos_raw: mov.datos_raw || null,
          });

        if (insertError) {
          errores.push(`Error inserting movement (${mov.fecha_operacion}, ${mov.importe}): ${insertError.message}`);
        } else {
          importados++;
        }
      } catch (movError: any) {
        errores.push(`Error processing movement: ${movError.message}`);
      }
    }

    // Log the import
    await supabaseAdmin.from('bank_import_logs').insert({
      bank_account_id: bank_account_id as string,
      tipo_importacion: formatoStr,
      archivo_nombre: fileName,
      movimientos_importados: importados,
      movimientos_duplicados: duplicados,
      errores: errores.length > 0 ? { mensajes: errores } : null,
    });

    // Update bank account saldo_actual if movements were imported
    if (importados > 0) {
      const { data: lastMov } = await supabaseAdmin
        .from('bank_movements')
        .select('saldo_posterior')
        .eq('bank_account_id', bank_account_id as string)
        .not('saldo_posterior', 'is', null)
        .order('fecha_operacion', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (lastMov?.saldo_posterior != null) {
        await supabaseAdmin
          .from('bank_accounts')
          .update({
            saldo_actual: lastMov.saldo_posterior,
            fecha_ultimo_sync: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', bank_account_id as string);
      }
    }

    return res.status(200).json({
      success: true,
      importados,
      duplicados,
      errores,
    });
  } catch (error: any) {
    console.error('[API bank/import] Unexpected error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error inesperado al importar movimientos.',
    });
  }
}

export default withAuth(handler);
